
import { useState } from "react";
import { Client } from "../types";
import { ClientCard } from "./ClientCard";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { SearchDialog } from "./SearchDialog";
import { useUserRoles } from "@/hooks/useUserRoles";

interface ClientsListProps {
  clients?: Client[];
  isLoading: boolean;
  onClientRemoved: () => void;
  onClientUpdated: () => void;
  onClientSelected: (client: Client) => void;
  isGuardView?: boolean;
}

export const ClientsList = ({ 
  clients, 
  isLoading, 
  onClientRemoved, 
  onClientUpdated, 
  onClientSelected,
  isGuardView = false
}: ClientsListProps) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const { isAdmin } = useUserRoles();

  const filteredClients = clients?.filter(client => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      (client.first_name?.toLowerCase().includes(query) || false) ||
      (client.last_name?.toLowerCase().includes(query) || false) ||
      (client.email?.toLowerCase().includes(query) || false) ||
      (client.company_name?.toLowerCase().includes(query) || false)
    );
  });
  
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(6)].map((_, index) => (
          <Skeleton key={index} className="h-48 rounded-xl" />
        ))}
      </div>
    );
  }
  
  if (!clients || clients.length === 0) {
    return (
      <Alert variant="default" className="bg-muted">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          No clients found.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <SearchDialog 
          onSearch={setSearchQuery} 
          searchQuery={searchQuery}
          placeholder="Search clients by name, email, or company..." 
        />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredClients?.map(client => (
          <ClientCard 
            key={client.id} 
            client={client}
            onClientRemoved={onClientRemoved}
            onClientUpdated={onClientUpdated}
            onClientSelected={onClientSelected}
            // Hide edit/delete actions for guard users
            hideActions={isGuardView}
          />
        ))}
      </div>
      
      {filteredClients?.length === 0 && (
        <Alert variant="default" className="bg-muted">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            No clients match your search criteria.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};
